import React from 'react';
import { Box, useTheme } from "@mui/material";
import AIEnergyIcon from "../styled/AIEnergyIcon";

export default function TemplateLoaderComponent({ minimal = false }) {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    
    // Get colors from theme
    const themeColors = {
        background: theme.palette.background.default,
        primary: theme.palette.primary.main,
        secondary: theme.palette.secondary.main,
        text: theme.palette.text.primary,
        textSecondary: theme.palette.text.secondary,
        border: theme.palette.divider,
        cyan: theme.palette.info?.main || '#06b6d4',
        emerald: theme.palette.success?.main || '#10b981',
        purple: theme.palette.augmentColor?.({ color: { main: '#8b5cf6' } }).main || '#8b5cf6',
    };
    
    const getBackgroundColor = () => {
        const opacity = minimal ? 'F2' : 'E6'; // 95% and 90% opacity
        return isDarkMode 
            ? `${themeColors.background}${opacity}`
            : minimal 
                ? 'rgba(255, 255, 255, 0.95)'
                : 'rgba(255, 255, 255, 0.9)';
    };
    
    const getTextColor = () => {
        return isDarkMode ? "text-gray-400" : "text-gray-600";
    };
    
    const getRingColor = () => {
        return isDarkMode 
            ? `border-[${themeColors.primary}]/20`
            : "border-blue-200/60";
    };
    
    const getReverseRingColor = () => {
        return isDarkMode 
            ? `border-[${themeColors.secondary}]/15`
            : "border-blue-300/40";
    };
    
    const getPulseColor = () => {
        return isDarkMode 
            ? `bg-[${themeColors.primary}]/10`
            : "bg-blue-100/50";
    };
    
    const getDotColor = () => {
        return isDarkMode 
            ? `bg-[${themeColors.cyan}]`
            : "bg-blue-400";
    };
    
    const getIconColor = () => {
        return isDarkMode ? themeColors.cyan : themeColors.primary;
    };
    
    if (minimal) {
        return (
            <Box 
                className="w-full h-full flex flex-col items-center justify-center absolute left-0 top-0 right-0 bottom-0 z-10"
                sx={{ 
                    backgroundColor: getBackgroundColor(),
                    backdropFilter: 'blur(2px)',
                }} 
            >
                {/* Simple Energy Pulse */}
                <div className="relative">
                    <div 
                        className={`absolute inset-0 -m-4 rounded-full ${getPulseColor()} animate-ping-simple`}
                        style={{
                            backgroundColor: isDarkMode 
                                ? `${themeColors.primary}20`
                                : undefined
                        }}
                    />
                    
                    <div className="relative z-10">
                        <AIEnergyIcon 
                            size="large"
                            animate={true}
                            isRotating={true}
                            color={getIconColor()}
                        />
                    </div>
                </div>
                
                <p className={`mt-4 text-xs font-medium ${getTextColor()}`}>
                    Loading...
                </p>
                
                <style jsx>{`
                    @keyframes ping-simple {
                        0% {
                            transform: scale(0.8);
                            opacity: 0.5;
                        }
                        100% {
                            transform: scale(1.5);
                            opacity: 0;
                        }
                    }
                    
                    .animate-ping-simple {
                        animation: ping-simple 1.5s ease-out infinite;
                    }
                `}</style>
            </Box>
        );
    }
    
    return (
        <Box 
            className="w-full h-full flex flex-col items-center justify-center absolute left-0 top-0 right-0 bottom-0 z-10"
            sx={{ 
                backgroundColor: getBackgroundColor(),
                backdropFilter: 'blur(3px)',
            }} 
        >
            {/* Classic Energy Loader */}
            <div className="relative">
                
                {/* Double Ring Effect */}
                <div className="absolute inset-0 -m-6">
                    <Box
                        className={`absolute inset-0 rounded-full border-3 animate-spin-slow`}
                        sx={{
                            borderColor: isDarkMode 
                                ? `${themeColors.primary}20`
                                : 'rgba(3, 3, 4, 0.15)',
                            borderTopColor: 'transparent',
                        }}
                    />
                    <Box
                        className={`absolute inset-0 rounded-full border-3 animate-spin-slow-reverse`}
                        sx={{
                            borderColor: isDarkMode 
                                ? `${themeColors.secondary}15`
                                : 'rgba(59, 130, 246, 0.1)',
                            borderRightColor: 'transparent',
                        }}
                    />
                </div>
                
                {/* Main Icon */}
                <div className="relative z-10 animate-bounce-subtle">
                    <AIEnergyIcon 
                        size="xlarge"
                        animate={true}
                        isRotating={true}
                        color={getIconColor()}
                    />
                </div>
                
                {/* Energy Dots */}
                <div className="absolute inset-0 -m-4 flex items-center justify-center">
                    {[0, 1, 2].map((i) => (
                        <Box
                            key={i}
                            className={`absolute w-2 h-2 rounded-full animate-orbit`}
                            sx={{
                                backgroundColor: getDotColor(),
                                animationDelay: `${i * 0.3}s`,
                                transform: `rotate(${i * 120}deg) translateX(20px)`,
                                boxShadow: isDarkMode 
                                    ? `0 0 4px ${themeColors.cyan}`
                                    : '0 0 4px rgba(59, 130, 246, 0.5)',
                            }}
                        />
                    ))}
                </div>
            </div>
            
            <p className={`mt-4 text-sm font-medium ${getTextColor()}`}>
                Loading...
            </p>
            
            <style jsx>{`
                @keyframes spin-slow {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                @keyframes spin-slow-reverse {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(-360deg); }
                }
                
                @keyframes bounce-subtle {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-4px); }
                }
                
                @keyframes orbit {
                    0% {
                        transform: rotate(0deg) translateX(20px) rotate(0deg);
                        opacity: ${isDarkMode ? '0.9' : '0.7'};
                    }
                    100% {
                        transform: rotate(360deg) translateX(20px) rotate(-360deg);
                        opacity: ${isDarkMode ? '0.4' : '0.3'};
                    }
                }
                
                .animate-spin-slow {
                    animation: spin-slow 3s linear infinite;
                }
                
                .animate-spin-slow-reverse {
                    animation: spin-slow-reverse 4s linear infinite;
                }
                
                .animate-bounce-subtle {
                    animation: bounce-subtle 1.5s ease-in-out infinite;
                }
                
                .animate-orbit {
                    animation: orbit 2s linear infinite;
                }
            `}</style>
        </Box>
    );
}